import React from "react";
import { Plus, X } from "lucide-react";

import { getAssetTags, replaceAssetTags } from "../../api/client";
import styles from "./assetTags.module.css";

const EMPTY_TAGS = [];

const DIALOG_TITLE = { add: "添加标签", edit: "编辑标签", list: "全部标签" };

function labelsOf(tags) {
  return tags.map((item) => item.label).filter(Boolean);
}

function normaliseLabels(value) {
  return [...new Set(String(value || "").split(/[，,\n]/).map((item) => item.trim()).filter(Boolean))];
}

// compact（概览表格）：标签 chip 是进标签汇总页的入口（onTagClick），只保留末尾「+」添加；
// 编辑 / 删除只在单标的页（非 compact）。
export function AssetTagEditor({ assetCode, initialTags, compact = false, inline = false, onChanged, onTagClick }) {
  const [tags, setTags] = React.useState(initialTags ?? EMPTY_TAGS);
  const [open, setOpen] = React.useState(false);
  const [draft, setDraft] = React.useState("");
  // "add" 加一个新的；"edit" 改**某一个**（editing 记住是哪个）；"list" 列出全部逐个处理。
  // 一个输入框装着「消费，老登股，行业龙头，酒类」整串重写，改一个要重打四个、
  // 删一个要自己找逗号——那不是编辑标签，是编辑字符串（2026-09-04 产品）。
  const [mode, setMode] = React.useState("add");
  const [editing, setEditing] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");

  React.useEffect(() => { setTags(initialTags ?? EMPTY_TAGS); }, [initialTags]);
  React.useEffect(() => {
    // undefined = the parent has not loaded tags; [] = authoritative empty result.
    if (initialTags !== undefined || !assetCode) return undefined;
    let cancelled = false;
    void getAssetTags(assetCode).then((payload) => {
      if (!cancelled) setTags(payload.tags ?? []);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [assetCode, initialTags]);

  function startAdding(event) {
    event?.stopPropagation();
    setMode("add");
    setDraft("");
    setError("");
    setOpen(true);
  }

  function startEditingOne(label) {
    return (event) => {
      event?.stopPropagation();
      setMode("edit");
      setEditing(label);
      setDraft(label);
      setError("");
      setOpen(true);
    };
  }

  function startListing(event) {
    event?.stopPropagation();
    setMode("list");
    setError("");
    setOpen(true);
  }

  async function commit(next) {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      const payload = await replaceAssetTags(assetCode, next);
      setTags(payload.tags ?? []);
      onChanged?.(payload.tags ?? []);
      return payload.tags ?? [];
    } catch (caught) {
      setError(caught.message || "标签未能保存。");
      return null;
    } finally {
      setBusy(false);
    }
  }

  async function removeLabel(label) {
    const next = await commit(labelsOf(tags).filter((item) => item !== label));
    if (next && next.length === 0) setOpen(false);
  }

  async function save(event) {
    event.preventDefault();
    const current = labelsOf(tags);
    // 接口是"整串替换"，所以这里自己把"加一个 / 改一个"折算成完整列表。
    // normaliseLabels 顺手去重，改重名不会出现两个一样的标签。
    const next = mode === "add"
      ? normaliseLabels([...current, draft].join("，"))
      : normaliseLabels(current.map((item) => (item === editing ? draft : item)).join("，"));
    if (await commit(next)) setOpen(false);
  }

  function openTag(label) {
    return (event) => {
      event?.stopPropagation();
      setOpen(false);
      onTagClick?.(label);
    };
  }

  const labels = labelsOf(tags);
  const signature = labels.join("");
  // **不预测，只收敛。** 先渲染全部，渲染完还横向溢出就少放一个，直到放得下。
  // 宽度是浏览器真实排版的结果，不是算出来的——2026-09-04 先后按"固定数量"和
  // "按字体宽度估算"两种预测法各改三轮，每轮都在某个视口下失效：字体宽度、
  // 「+」和「+N」的实际宽度、容器宽度的测量时机，任何一项差几个像素就翻车。
  //
  // 用数字（不是 null）当状态：宽度变化时 setCount(labels.length) 才一定触发重渲染。
  // 之前用 null 当"待测量"哨兵，而 setCount(null) 在已经是 null 时是空操作，
  // 于是整个机制卡死在 null，fit 从头到尾没生效过。
  const [count, setCount] = React.useState(labels.length);
  const listRef = React.useRef(null);
  const widthRef = React.useRef(0);
  const stepsRef = React.useRef(0);

  React.useLayoutEffect(() => {
    if (!compact) return undefined;
    const element = listRef.current;
    if (!element) return undefined;
    const observer = new ResizeObserver(() => {
      const width = element.clientWidth;
      if (width === widthRef.current) return;
      widthRef.current = width;
      stepsRef.current = 0;
      setCount(labels.length);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [compact, labels.length]);

  React.useLayoutEffect(() => {
    stepsRef.current = 0;
    setCount(labels.length);
  }, [signature]);

  // 按**高度**收敛：这一格是两行换行（max-height:48px），放不下时溢出的是高度，
  // 横向永远不溢出——按宽度判会一次都不触发，标签照样被裁成三行。
  // stepsRef 是硬上限：「渲染→量→再渲染」这种自反馈一旦不收敛就是
  // Maximum update depth，整页白屏。宁可少显示一个标签，也不能把页面搞挂。
  React.useLayoutEffect(() => {
    if (!compact || count <= 0) return;
    if (stepsRef.current >= labels.length) return;
    const element = listRef.current;
    if (!element) return;
    if (element.scrollHeight - element.clientHeight > 1) {
      stepsRef.current += 1;
      setCount(count - 1);
    }
  });

  const visible = compact
    ? labels.slice(0, Math.min(count, labels.length))
    : inline && labels.length > 4
      ? labels.slice(0, 3)
      : labels;
  return (
    <span ref={listRef} className={`${styles.tags} ${compact ? styles.compact : ""} ${inline ? styles.inline : ""}`}>
      {visible.map((label) => compact
        ? <button type="button" data-tag-chip className={styles.tag} key={label} onClick={openTag(label)} title={`查看「${label}」汇总`}>{label}</button>
        : <button type="button" data-tag-chip className={styles.tag} key={label} onClick={startEditingOne(label)} title={`编辑「${label}」`}>{label}</button>)}
      {labels.length > visible.length && <button type="button" className={styles.more} onClick={startListing} title="查看全部标签">+{labels.length - visible.length}</button>}
      {/* 「+」永远在。2026-09-02 曾按原型截图把它在"已有标签时"藏起来，于是加完
          第一个标签之后就再也加不了第二个——原型只截到已有标签的样子，没截过
          「想再加一个」那一刻。点已有标签也能进编辑，但那不叫入口，没人猜得到。 */}
      <button
        type="button"
        data-tag-add
        className={`${styles.add} ${labels.length === 0 ? styles.addEmpty : ""}`}
        onClick={startAdding}
        aria-label="添加标签"
        title="添加标签（点已有标签可以单独修改）"
      >
        <Plus size={13} />
        {labels.length === 0 && <span>添加标签</span>}
      </button>
      {open && (
        <span className={styles.backdrop} role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setOpen(false); }}>
          <form className={styles.dialog} onSubmit={save} onMouseDown={(event) => event.stopPropagation()} aria-label={DIALOG_TITLE[mode]}>
            <button type="button" className={styles.close} onClick={() => setOpen(false)} aria-label="关闭"><X size={16} /></button>
            <strong>{DIALOG_TITLE[mode]}</strong>
            {mode === "list" ? (
              // 一行一个标签，各自删各自的。想改内容点标签名进单个编辑。
              <ul className={styles.manageList}>
                {labels.map((label) => (
                  <li key={label}>
                    <button type="button" onClick={compact ? openTag(label) : startEditingOne(label)}>{label}</button>
                    {!compact && (
                      <button
                        type="button"
                        className={styles.removeTag}
                        onClick={() => { void removeLabel(label); }}
                        disabled={busy}
                        aria-label={`删除标签 ${label}`}
                      ><X size={14} /></button>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <input
                autoFocus
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                placeholder={mode === "add" ? "例如：高股息" : "改成新的名字"}
                aria-label={mode === "add" ? "新标签" : "标签名"}
              />
            )}
            {error && <em>{error}</em>}
            {mode !== "list" && (
              <div className={styles.dialogActions}>
                {mode === "edit" && (
                  <button
                    type="button"
                    className={styles.deleteButton}
                    onClick={() => { void removeLabel(editing); }}
                    disabled={busy}
                  >删除这个标签</button>
                )}
                <button type="submit" disabled={busy || !draft.trim()}>
                  {busy ? "正在保存…" : mode === "add" ? "添加" : "保存"}
                </button>
              </div>
            )}
          </form>
        </span>
      )}
    </span>
  );
}
